// Cars: three dealerships, normal -> super. Buy, own, drive (driving is render-side).
// All makes and models are fictional (no real brands).

import type { Rng } from "../rng";
import { dayOf, pushLog, type GameState } from "../state";
import { playerClout } from "../player";
import { TUNABLES } from "@degen/shared/config/tunables";
import { money } from "@degen/shared/money";

export type CarTier = "normal" | "sport" | "luxury" | "exotic" | "super";
export type DealerId = "polancco" | "abbja" | "mrjaay" | "zoomers";
export type CarBody = "hatch" | "sedan" | "van" | "suv" | "coupe" | "pickup" | "super";
export type BikeBody = "scooter" | "okada" | "dirtbike" | "cruiser" | "sportbike" | "superbike";
export type VehicleBody = CarBody | BikeBody;
export const BIKE_BODIES: readonly BikeBody[] = ["scooter", "okada", "dirtbike", "cruiser", "sportbike", "superbike"];
export const isBike = (body: VehicleBody): body is BikeBody => (BIKE_BODIES as readonly string[]).includes(body);

export interface CarModel {
  id: string;
  make: string;
  model: string;
  tier: CarTier;
  dealer: DealerId;
  body: VehicleBody;
  price: number;
  topSpeed: number; // world units per second
  accel: number;
  handling: number; // 0.5-1.4 steering responsiveness
  colors: string[];
}

export const DEALERS: Record<DealerId, { name: string; tagline: string }> = {
  polancco: { name: "Polancco", tagline: "Everyday rides and hot hatches. First car energy." },
  abbja: { name: "Abbja Cars", tagline: "Luxury and exotics for people whose bags went up." },
  mrjaay: { name: "Mr Jaay Autos", tagline: "Exotics and supercars. If you have to ask, you can't afford it." },
  zoomers: { name: "Zoomers Moto", tagline: "Two wheels, zero traffic. Scooters to superbikes." },
};

const C = { red: "#dc2626", blue: "#2563eb", black: "#111827", white: "#f4f4f5", silver: "#9ca3af", yellow: "#facc15", green: "#16a34a", orange: "#f97316", purple: "#7c3aed", pink: "#ec4899", teal: "#0d9488", gold: "#d4a017" };

export const CARS: CarModel[] = [
  // Polancco — normal & sport
  { id: "bodaa", make: "Polancco", model: "Bodaa", tier: "normal", dealer: "polancco", body: "hatch", price: 6_000, topSpeed: 11, accel: 4, handling: 1.1, colors: [C.white, C.red, C.blue, C.yellow] },
  { id: "danfo", make: "Polancco", model: "Danfo Van", tier: "normal", dealer: "polancco", body: "van", price: 9_000, topSpeed: 10, accel: 3.2, handling: 0.8, colors: [C.yellow, C.white] },
  { id: "kekra", make: "Kekra", model: "Sedan", tier: "normal", dealer: "polancco", body: "sedan", price: 12_000, topSpeed: 12.5, accel: 4.2, handling: 1, colors: [C.silver, C.black, C.white, C.blue] },
  { id: "moonwagon", make: "Moonwagon", model: "Trail SUV", tier: "normal", dealer: "polancco", body: "suv", price: 18_000, topSpeed: 12, accel: 4, handling: 0.9, colors: [C.green, C.black, C.silver] },
  { id: "zippa", make: "Zippa", model: "GTI", tier: "sport", dealer: "polancco", body: "hatch", price: 28_000, topSpeed: 15, accel: 6, handling: 1.3, colors: [C.red, C.white, C.black] },
  { id: "bullrun", make: "Bullrun", model: "GT", tier: "sport", dealer: "polancco", body: "coupe", price: 45_000, topSpeed: 17, accel: 7, handling: 1.2, colors: [C.blue, C.orange, C.black] },
  // Abbja Cars — luxury & exotic
  { id: "regent", make: "Abbja", model: "Regent", tier: "luxury", dealer: "abbja", body: "sedan", price: 80_000, topSpeed: 16, accel: 6, handling: 1, colors: [C.black, C.silver, C.white] },
  { id: "highlander", make: "Abbja", model: "Highlander", tier: "luxury", dealer: "abbja", body: "suv", price: 110_000, topSpeed: 15, accel: 5.5, handling: 0.9, colors: [C.black, C.white, C.gold] },
  { id: "velvetto", make: "Velvetto", model: "S Coupe", tier: "luxury", dealer: "abbja", body: "coupe", price: 160_000, topSpeed: 18, accel: 7.5, handling: 1.15, colors: [C.purple, C.black, C.silver] },
  { id: "corsaro", make: "Corsaro", model: "Spyder", tier: "exotic", dealer: "abbja", body: "super", price: 260_000, topSpeed: 21, accel: 9, handling: 1.25, colors: [C.yellow, C.red, C.green] },
  { id: "valkoro", make: "Valkoro", model: "V12", tier: "exotic", dealer: "abbja", body: "coupe", price: 380_000, topSpeed: 22, accel: 9.5, handling: 1.2, colors: [C.red, C.black, C.white] },
  { id: "mistral", make: "Mistral", model: "GTO", tier: "exotic", dealer: "abbja", body: "super", price: 450_000, topSpeed: 23, accel: 10, handling: 1.3, colors: [C.orange, C.teal, C.silver] },
  // Mr Jaay Autos — exotic & super
  { id: "lagosnight", make: "Jaay", model: "Lagos Night", tier: "exotic", dealer: "mrjaay", body: "coupe", price: 520_000, topSpeed: 23, accel: 10, handling: 1.25, colors: [C.black, C.purple, C.gold] },
  { id: "diablo", make: "Degen", model: "Diablo", tier: "super", dealer: "mrjaay", body: "super", price: 750_000, topSpeed: 26, accel: 12, handling: 1.3, colors: [C.green, C.orange, C.yellow] },
  { id: "laserbolt", make: "Laserbolt", model: "X", tier: "super", dealer: "mrjaay", body: "super", price: 1_100_000, topSpeed: 28, accel: 13, handling: 1.35, colors: [C.red, C.white, C.teal] },
  { id: "veyrox", make: "Hypercoin", model: "Veyrox", tier: "super", dealer: "mrjaay", body: "super", price: 1_600_000, topSpeed: 31, accel: 14, handling: 1.3, colors: [C.blue, C.black, C.silver] },
  { id: "hyperion", make: "Moonshot", model: "Hyperion", tier: "super", dealer: "mrjaay", body: "super", price: 2_400_000, topSpeed: 33, accel: 15, handling: 1.4, colors: [C.pink, C.purple, C.white] },
  { id: "w1", make: "Whale", model: "Edition W1", tier: "super", dealer: "mrjaay", body: "super", price: 5_000_000, topSpeed: 36, accel: 16, handling: 1.4, colors: [C.gold, C.black] },
  // Zoomers Moto — motorcycles (quick, nimble, cheap to start)
  { id: "zippy", make: "Zoomers", model: "Zippy 125", tier: "normal", dealer: "zoomers", body: "scooter", price: 2_500, topSpeed: 10, accel: 5, handling: 1.45, colors: [C.teal, C.pink, C.white, C.yellow] },
  { id: "okada", make: "Zoomers", model: "Okada Classic", tier: "normal", dealer: "zoomers", body: "okada", price: 3_500, topSpeed: 11.5, accel: 5.5, handling: 1.4, colors: [C.red, C.black, C.blue] },
  { id: "trail", make: "Mudlark", model: "Trail 250", tier: "sport", dealer: "zoomers", body: "dirtbike", price: 7_000, topSpeed: 13.5, accel: 7, handling: 1.5, colors: [C.orange, C.green, C.white] },
  { id: "highwayking", make: "Ironhorse", model: "Highway King", tier: "luxury", dealer: "zoomers", body: "cruiser", price: 22_000, topSpeed: 16, accel: 6.5, handling: 1.05, colors: [C.black, C.red, C.silver] },
  { id: "shuriken", make: "Kaze", model: "Shuriken 600", tier: "exotic", dealer: "zoomers", body: "sportbike", price: 38_000, topSpeed: 23, accel: 11, handling: 1.4, colors: [C.green, C.blue, C.red] },
  { id: "cometrr", make: "Kaze", model: "Comet RR", tier: "super", dealer: "zoomers", body: "superbike", price: 95_000, topSpeed: 30, accel: 14, handling: 1.35, colors: [C.black, C.yellow, C.white] },
];

export const TIER_LABEL: Record<CarTier, string> = { normal: "Normal", sport: "Sport", luxury: "Luxury", exotic: "Exotic", super: "Supercar" };

export interface OwnedCar {
  id: string;
  modelId: string;
  color: string;
  boughtDay: number;
  paid: number;
}

export interface CarsState {
  owned: OwnedCar[];
  activeId: string | null;
  nextId: number;
  /** Rides you've given (optional: older saves don't have it). */
  rides?: { day: number; paidToday: number; total: number; earned: number };
}

export function createCars(): CarsState {
  return { owned: [], activeId: null, nextId: 1 };
}

export function carModel(id: string): CarModel {
  return CARS.find((c) => c.id === id)!;
}

/** Resale value: 80% off the lot, then 1% a day down to half price. */
export function carValue(car: OwnedCar, day: number): number {
  const age = Math.max(0, day - car.boughtDay);
  return car.paid * Math.max(0.5, 0.8 - 0.01 * age);
}

export function carsValue(state: GameState): number {
  const day = dayOf(state.tick);
  return state.cars.owned.reduce((a, c) => a + carValue(c, day), 0);
}

export type CarResult = { ok: true; data?: Record<string, number> } | { ok: false; error: string };

export function buyCar(state: GameState, rng: Rng, modelId: string, color: string): CarResult {
  const m = CARS.find((c) => c.id === modelId);
  const p = state.player;
  if (!m) return { ok: false, error: "That car isn't on the lot." };
  if (!m.colors.includes(color)) color = m.colors[0];
  if (p.cash < m.price) return { ok: false, error: `The ${m.make} ${m.model} costs ${money(m.price)}.` };
  if (state.cars.owned.length >= 6) return { ok: false, error: "Your garage is full (6 cars). Sell one first." };
  p.cash -= m.price;
  const car: OwnedCar = { id: `car${state.cars.nextId++}`, modelId, color, boughtDay: dayOf(state.tick), paid: m.price };
  state.cars.owned.push(car);
  state.cars.activeId = car.id;
  // Flex post: new whip, new followers (bigger flex for pricier cars, scaled by your reach).
  const reach = Math.pow(playerClout(p) + TUNABLES.social.baseReach, TUNABLES.social.reachExp);
  const gained = Math.round(reach * Math.sqrt(m.price / 10_000) * 0.25 * rng.range(0.6, 1.4) * (p.trust / 100));
  p.followers += gained;
  pushLog(state, "car_bought", `🏎️ New whip! You bought a ${m.make} ${m.model}. The flex post got you +${gained.toLocaleString()} followers.`, { car: car.id });
  return { ok: true, data: { followers: gained } };
}

export function sellCar(state: GameState, carId: string): CarResult {
  const car = state.cars.owned.find((c) => c.id === carId);
  if (!car) return { ok: false, error: "No such car." };
  const value = carValue(car, dayOf(state.tick));
  state.player.cash += value;
  state.cars.owned = state.cars.owned.filter((c) => c !== car);
  if (state.cars.activeId === carId) state.cars.activeId = state.cars.owned[0]?.id ?? null;
  return { ok: true, data: { usd: value } };
}

export function setActiveCar(state: GameState, carId: string): CarResult {
  if (!state.cars.owned.some((c) => c.id === carId)) return { ok: false, error: "No such car." };
  state.cars.activeId = carId;
  return { ok: true };
}
